import { useState, useEffect } from 'react';
import { getAuthHeaders } from '../auth';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8788';

interface Page {
  id: string;
  title: string;
  icon: string | null;
  parent_id: string | null;
  position: number;
}

export function usePages() {
  const [pages, setPages] = useState<Page[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPages();
  }, []);

  async function fetchPages() {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/v1/pages`, {
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to fetch pages: ${res.status}`);
      }

      const data = await res.json();
      setPages(data.pages || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }

  async function createPage(data: Partial<Page>): Promise<Page | null> {
    try {
      const res = await fetch(`${API_URL}/v1/pages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        throw new Error(`Failed to create page: ${res.status}`);
      }

      const result = await res.json();
      const newPage = result.page;

      setPages((prev) => [...prev, newPage]);
      return newPage;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      return null;
    }
  }

  async function updatePage(id: string, data: Partial<Page>): Promise<void> {
    try {
      const res = await fetch(`${API_URL}/v1/pages/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        throw new Error(`Failed to update page: ${res.status}`);
      }

      const result = await res.json();
      setPages((prev) =>
        prev.map((p) => (p.id === id ? result.page : p))
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  }

  async function deletePage(id: string): Promise<void> {
    try {
      const res = await fetch(`${API_URL}/v1/pages/${id}`, {
        method: 'DELETE',
        headers: {
          ...getAuthHeaders(),
        },
      });

      if (!res.ok) {
        throw new Error(`Failed to delete page: ${res.status}`);
      }

      setPages((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    }
  }

  return {
    pages,
    loading,
    error,
    fetchPages,
    createPage,
    updatePage,
    deletePage,
  };
}
